/*
 * (C) 2026 BeyondDemise, All Rights Reserved.
 *
 * Bearer-token-injecting fetch wrapper. Copied and trimmed from
 * app-frontend's `src/services/tdfetch.ts`: the single-flight transparent
 * refresh-on-401 behaviour, minus the submit-pipeline contract gate and the
 * crypto key-fetch helpers (app-admin makes only read-only GETs).
 */

import { authService } from './auth.js';

type TdFetchOptions = Omit<RequestInit, 'headers'> & {
  headers?: Record<string, string>;
};

/** Single-flight wrapper so parallel 401s share one refresh (app-frontend #114). */
let refreshInFlight: Promise<void> | null = null;

const sharedRefresh = (): Promise<void> => {
  if (refreshInFlight === null) {
    refreshInFlight = authService
      .refreshAccessToken()
      .then(() => undefined)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
};

/**
 * Bearer-injecting fetch. On a 401, attempts a single transparent token
 * refresh and retries once; on a second 401 or a failed refresh, returns the
 * original 401. Non-recursive — at most one extra round-trip per call.
 */
export const tdFetch = async (
  url: string,
  options: TdFetchOptions = {}
): Promise<Response> => {
  const token = authService.getAccessToken();
  if (!token) {
    throw new Error('No token available');
  }

  const buildInit = (bearerToken: string): RequestInit => ({
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${bearerToken}`,
      'Content-Type': 'application/json',
    },
  });

  const response = await fetch(url, buildInit(token));
  if (response.status !== 401) {
    return response;
  }

  let refreshed: string | null = null;
  try {
    await sharedRefresh();
    refreshed = authService.getAccessToken();
  } catch {
    return response;
  }
  if (!refreshed || refreshed === token) {
    return response;
  }
  const retry = await fetch(url, buildInit(refreshed));
  return retry.status === 401 ? response : retry;
};
